import { Alert, Col, Flex, InputNumber, Radio, Row } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import RefinementChart from './chart';
import CoreyExponentsTable from './coreyExpoentsTable';
import RefinementTable from './table';
import { useAppContext } from '../../../../../providers/AppContext';
import { useEffect, useState } from 'react';
import { Accordion } from '../../../../../design-system/accordion';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateKroKrwIntersection } from './krIntersection';
import { verifyValue } from '../../../specialCore';
import {
  CorrelationComparisonMode,
  CorrelationEndpointSource
} from '../../../../../@types/projects/types';
import {
  ENDPOINT_COMPARISON_MODE_LABELS,
  ENDPOINT_SOURCE_LABELS
} from '../../../../../design-system/correlation/utils/endpointSourceConstants';
import { useCorrelation } from '../../../../../providers/Correlation';
import {
  buildWaterOilRefinementCurves,
  clampCurvePointCount,
  MAX_CURVE_POINTS,
  MIN_CURVE_POINTS
} from '../../../../utils/curveGeneration';
import { resolveWaterOilValues } from './valueResolver';

interface ChartData {
  /** Stable id so Highcharts matches series by id (not array index) across
   *  in-place updates — required since overlay mode prepends series. */
  id: string;
  name: string;
  data: number[][];
  color: string;
  lineWidth: number;
  marker?: {
    symbol?: string;
    enabled: boolean;
  };
}

const format5 = (value: number | string | null | undefined) => {
  if (value == null) {
    return verifyValue(value as any);
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return verifyValue(value as any);
  }

  return numeric.toFixed(5);
};

const SOURCE_ALERT_COLORS: Record<CorrelationEndpointSource, string> = {
  lab: '#8a8a8a',
  refinement: '#3178f6'
};

const WaterOilRefinement = () => {
  const { selectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { updateConfigStates } = useAppContext();
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const sampleCurves = selectedInfo?.sample?.input_sample_curves?.water_oil;
  const kRelCurves = selectedInfo?.sample?.k_rel_curves?.water_oil;
  const [endpointSource, setEndpointSource] =
    useState<CorrelationEndpointSource>('lab');
  const [comparisonMode, setComparisonMode] =
    useState<CorrelationComparisonMode>('off');
  const [numberOfPoints, setNumberOfPoints] = useState(30);
  const { appliedEndpointSource } = useCorrelation();

  const swKrel = sampleCurves?.sw_krel ?? [];
  const labResolved = resolveWaterOilValues(selectedInfo?.sample, 'lab');
  const refinementResolved = resolveWaterOilValues(selectedInfo?.sample, 'refinement');
  const swi = labResolved.swi ?? 0;
  const no_w = labResolved.no ?? 0;
  const nw = labResolved.nw ?? 0;
  const krwEndpoint = labResolved.krwEndpoint ?? 0;

  const labSon = kRelCurves?.son ?? [];
  const labSwn = kRelCurves?.swn ?? [];
  const labKro = labSon.map(s => (s !== null ? Math.pow(Math.max(s, 0), no_w) : null));
  const labKrw = labSwn.map(s => (s !== null ? krwEndpoint * Math.pow(Math.max(s, 0), nw) : null));

  const labGenerated = buildWaterOilRefinementCurves({
    swi: labResolved.swi ?? 0,
    sor: labResolved.sor ?? 0,
    no: labResolved.no ?? 0,
    nw: labResolved.nw ?? 0,
    krwEndpoint: labResolved.krwEndpoint ?? 0,
    kroEndpoint: labResolved.kroEndpoint,
    numberOfPoints
  });
  const refinementGenerated = buildWaterOilRefinementCurves({
    swi: refinementResolved.swi ?? 0,
    sor: refinementResolved.sor ?? 0,
    no: refinementResolved.no ?? 0,
    nw: refinementResolved.nw ?? 0,
    krwEndpoint: refinementResolved.krwEndpoint ?? 0,
    kroEndpoint: refinementResolved.kroEndpoint,
    numberOfPoints
  });

  const getSourceValues = (source: CorrelationEndpointSource) => {
    if (source === 'refinement') {
      return {
        sw: refinementGenerated.sw,
        krw: refinementGenerated.krw,
        kro: refinementGenerated.kro
      };
    }
    return {
      sw: labGenerated.sw.length ? labGenerated.sw : swKrel,
      krw: labGenerated.krw.length ? labGenerated.krw : labKrw,
      kro: labGenerated.kro.length ? labGenerated.kro : labKro
    };
  };

  const selectedValues = getSourceValues(endpointSource);
  const getValidationMessages = (
    source: CorrelationEndpointSource
  ): string[] => {
    const sourceValues = getSourceValues(source);
    const swValues = sourceValues.sw ?? [];
    const resolved = resolveWaterOilValues(selectedInfo?.sample, source);
    const sorValue = resolved.sor;
    const krwEndpoint = resolved.krwEndpoint;

    const sourceLabel = source === 'lab' ? 'Lab' : 'Refinement';
    const warnings: string[] = [];
    const intersectionStatus = calculateKroKrwIntersection({
      swValues,
      swi: resolved.swi ?? swValues?.[0],
      sor: sorValue,
      no: resolved.no,
      nw: resolved.nw,
      krwEndPoint: krwEndpoint
    });

    if (intersectionStatus.status === 'invalid-input') {
      warnings.push(
        `[${sourceLabel}] Invalid inputs for refinement calculations. Check Swi/Sor values.`
      );
    } else if (intersectionStatus.invalidRange) {
      warnings.push(
        `[${sourceLabel}] Some Sw values are outside the valid range [Swi, 1 - Sor].`
      );
    }

    const finiteSwValues = swValues.filter(v => v != null);
    const maxSw =
      finiteSwValues.length > 0 ? Math.max(...finiteSwValues) : null;
    const expectedSor = maxSw == null ? null : 1 - maxSw;

    if (
      expectedSor != null &&
      sorValue != null &&
      Math.abs(expectedSor - sorValue) > 1e-4
    ) {
      warnings.push(
        `[${sourceLabel}] Sor consistency check failed: Sor=${sorValue.toFixed(
          4
        )}, expected (1 - max(Sw))=${expectedSor.toFixed(4)}.`
      );
    }

    return warnings;
  };

  const validationMessages = [
    ...getValidationMessages('lab'),
    ...getValidationMessages('refinement')
  ];

  useEffect(() => {
    updateConfigStates({ showRefinementTable: true });
  }, []);

  const tableColumns = [
    { title: `Sw`, dataIndex: 'Sw', key: 'Sw', render: format5 },
    { title: `Krw Calc`, dataIndex: 'Krw', key: 'Krw', render: format5 },
    { title: `Kro Calc`, dataIndex: 'Kro', key: 'Kro', render: format5 }
  ];

  const tableData = selectedValues.sw?.map((sw, index) => ({
    Sw: sw,
    Krw: selectedValues.krw?.[index],
    Kro: selectedValues.kro?.[index]
  }));

  const getChartData = () => {
    const kroCalcData: number[][] = [];
    const krwCalcData: number[][] = [];
    const kroLabData: number[][] = [];
    const krwLabData: number[][] = [];

    const sw = selectedValues.sw ?? [];
    const kroCalc = selectedValues.kro ?? [];
    const krwCalc = selectedValues.krw ?? [];

    const secondarySource: CorrelationEndpointSource =
      endpointSource === 'lab' ? 'refinement' : 'lab';
    const secondaryValues = getSourceValues(secondarySource);
    const secondaryKroData: number[][] = [];
    const secondaryKrwData: number[][] = [];

    const swKrel = sampleCurves?.sw_krel ?? [];
    const kroLab = sampleCurves?.kro_lab ?? [];
    const krgLab = sampleCurves?.krw_lab ?? [];

    if (sw.length && kroCalc.length && krwCalc.length) {
      sw.forEach((_, index) => {
        const kroVal = kroCalc[index];
        const krwVal = krwCalc[index];
        if (kroVal != null) kroCalcData.push([sw[index], kroVal]);
        if (krwVal != null) krwCalcData.push([sw[index], krwVal]);
      });
    }

    if (swKrel.length && kroLab.length && krgLab.length) {
      swKrel.forEach((_, index) => {
        kroLabData.push([swKrel[index], kroLab[index]]);
        krwLabData.push([swKrel[index], krgLab[index]]);
      });
    }

    const currentChartData: ChartData[] = [
      {
        id: 'kro-calc',
        name: `Kro Calc (${ENDPOINT_SOURCE_LABELS[endpointSource]})`,
        data: kroCalcData,
        color: '#059e05',
        lineWidth: 3,
        marker: { enabled: false }
      },
      {
        id: 'krw-calc',
        name: `Krw Calc (${ENDPOINT_SOURCE_LABELS[endpointSource]})`,
        data: krwCalcData,
        color: '#3c85fa',
        lineWidth: 3,
        marker: { enabled: false }
      },
      {
        id: 'kro-lab',
        name: 'Kro Lab',
        data: kroLabData,
        color: '#059e05',
        lineWidth: 0,
        marker: {
          symbol: 'circle',
          enabled: true
        }
      },
      {
        id: 'krw-lab',
        name: 'Krw Lab',
        data: krwLabData,
        color: '#3c85fa',
        lineWidth: 0,
        marker: {
          symbol: 'circle',
          enabled: true
        }
      }
    ];

    if (comparisonMode === 'overlay') {
      (secondaryValues.sw ?? []).forEach((_, index) => {
        const swValue = secondaryValues.sw?.[index];
        const kroValue = secondaryValues.kro?.[index];
        const krwValue = secondaryValues.krw?.[index];
        if (swValue != null && kroValue != null) {
          secondaryKroData.push([swValue, kroValue]);
        }
        if (swValue != null && krwValue != null) {
          secondaryKrwData.push([swValue, krwValue]);
        }
      });

      currentChartData.unshift(
        {
          id: 'kro-calc-secondary',
          name: `Kro Calc (${ENDPOINT_SOURCE_LABELS[secondarySource]})`,
          data: secondaryKroData,
          color: 'rgba(120,120,120,0.45)',
          lineWidth: 2,
          marker: { enabled: false }
        },
        {
          id: 'krw-calc-secondary',
          name: `Krw Calc (${ENDPOINT_SOURCE_LABELS[secondarySource]})`,
          data: secondaryKrwData,
          color: 'rgba(120,120,120,0.30)',
          lineWidth: 2,
          marker: { enabled: false }
        }
      );
    }

    setChartData(currentChartData);
  };

  useEffect(() => {
    getChartData();
  }, [selectedInfo, endpointSource, comparisonMode, numberOfPoints]);

  useEffect(() => {
    setEndpointSource(appliedEndpointSource);
  }, [appliedEndpointSource]);

  return (
    <Accordion defaultOpen="refinement-water-oil">
      <Accordion.Item
        id="refinement-water-oil"
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
            <span>Refinement</span>
            <span style={{ color: colors.waterOil }}>Water-Oil</span>
          </div>
        }
      >
        {kRelCurves?.son?.length ? (
          <div style={{ padding: '0.5rem' }}>
            <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={endpointSource}
                onChange={event => {
                  const nextSource = event.target.value as CorrelationEndpointSource;
                  setEndpointSource(nextSource);
                }}
                style={{ marginRight: '0.5rem' }}
              >
                <Radio.Button value="lab">
                  {ENDPOINT_SOURCE_LABELS.lab}
                </Radio.Button>
                <Radio.Button value="refinement">
                  {ENDPOINT_SOURCE_LABELS.refinement}
                </Radio.Button>
              </Radio.Group>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={comparisonMode}
                onChange={event => {
                  const nextMode = event.target.value as CorrelationComparisonMode;
                  setComparisonMode(nextMode);
                }}
              >
                <Radio.Button value="off">
                  {ENDPOINT_COMPARISON_MODE_LABELS.off}
                </Radio.Button>
                <Radio.Button value="overlay">
                  {ENDPOINT_COMPARISON_MODE_LABELS.overlay}
                </Radio.Button>
              </Radio.Group>
              <Flex align="center" gap="small" style={{ marginLeft: '0.5rem' }}>
                <span style={{ fontSize: '12px' }}>Curve points:</span>
                <InputNumber
                  min={MIN_CURVE_POINTS}
                  max={MAX_CURVE_POINTS}
                  step={10}
                  value={numberOfPoints}
                  onChange={value => {
                    if (value != null) {
                      setNumberOfPoints(clampCurvePointCount(value));
                    }
                  }}
                  size="small"
                  style={{ width: 72 }}
                />
              </Flex>
            </div>
            {validationMessages.map(messageText => {
              const source: CorrelationEndpointSource = messageText.includes(
                '[Refinement]'
              )
                ? 'refinement'
                : 'lab';
              return (
                <Alert
                  key={messageText}
                  message={messageText}
                  type="warning"
                  showIcon
                  style={{
                    marginBottom: '0.5rem',
                    borderLeft: `3px solid ${SOURCE_ALERT_COLORS[source]}`
                  }}
                />
              );
            })}
            <Row>
              <Col span={7}>
                <RefinementTable
                  columns={tableColumns}
                  data={tableData}
                  bordered={false}
                />
              </Col>
              <Col span={6}>
                <CoreyExponentsTable endpointSource={endpointSource} />
              </Col>
              <Col span={11}>
                <RefinementChart
                  data={chartData}
                  endpointSource={endpointSource}
                  comparisonMode={comparisonMode}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <div className="no-data-div">No refinement curves generated yet. Click <strong>Calculate</strong> in the Laboratory Data section to compute Corey exponents and refinement curves.</div>
        )}
      </Accordion.Item>
    </Accordion>
  );
};

export default WaterOilRefinement;
